/**
 * MaterialityThresholds — configure the impact / financial materiality
 * cut-offs on the 1-25 IRO scoring scale (5x5 severity x likelihood grid).
 *
 * Endpoints (mounted at /api by server.py:439):
 *   GET    /api/materiality/config/resolve?framework=&entity_id=
 *   GET    /api/materiality/config?framework=&entity_id=
 *   GET    /api/materiality/config/history?framework=&entity_id=
 *   POST   /api/materiality/config            (create / new version)
 *   POST   /api/materiality/config/seed-default
 *   DELETE /api/materiality/config/{config_id}   (supersede)
 *
 * Not entity-scoped: entity is an OPTIONAL filter — framework-level and
 * global-default configs exist with no entity, so the page never blocks on a
 * missing entity selection.
 */
import { useCallback, useEffect, useState } from "react";
import {
  SlidersHorizontal, Building2, Layers, Globe, Plus, Sprout,
  ChevronDown, ChevronRight, History as HistoryIcon,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Badge, Data, Btn, DataTable,
  ErrorBanner, Skeleton, field, Label, apiFetch, C, MONO, SANS,
} from "../../components/esg/EsgKit";
import { useEntities } from "../../hooks/useEntities";

const BASE = "/api/materiality/config";
const SCALE_MIN = 1;
const SCALE_MAX = 25;

// Framework-level list rows carry entity_id === null.
const entityLabel = (id) =>
  id ? <Data color={C.body}>{id}</Data>
     : <span style={{ fontFamily: MONO, fontSize: 12, color: C.muted }}>— framework default</span>;

// source ∈ entity | framework | global_default | default
function SourceBadge({ source }) {
  if (source === "entity")
    return <Badge c={C.primary} Icon={Building2} label="Entity override" />;
  if (source === "framework")
    return <Badge c={C.locked} Icon={Layers} label="Framework default" />;
  return <Badge c={C.neutral} Icon={Globe} label="Global default (9/9)" />;
}

export default function MaterialityThresholds() {
  const { entities, entityId, setEntityId } = useEntities("materiality_cfg_entity_id");

  const [framework, setFramework] = useState("esrs");
  const [frameworkDraft, setFrameworkDraft] = useState("esrs");
  const [resolved, setResolved] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // History (collapsible, loaded on expand)
  const [histOpen, setHistOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // Create form
  const [form, setForm] = useState({
    framework: "esrs", entity_id: "", impact_threshold: "9",
    financial_threshold: "9", effective_date: new Date().toISOString().slice(0, 10),
  });
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const qs = useCallback(() => {
    const p = new URLSearchParams({ framework });
    if (entityId) p.set("entity_id", entityId);
    return p.toString();
  }, [framework, entityId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [res, list] = await Promise.all([
        apiFetch(`${BASE}/resolve?${qs()}`),
        apiFetch(`${BASE}?${qs()}`),
      ]);
      setResolved(res);
      setConfigs(list?.configs || []);
    } catch (e) {
      setError(e.message || "Failed to load threshold configuration");
    } finally {
      setLoading(false);
    }
  }, [qs]);

  useEffect(() => { load(); }, [load]);

  // Keep the create-form framework aligned with the active filter.
  useEffect(() => { setF("framework", framework); /* eslint-disable-next-line */ }, [framework]);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const p = new URLSearchParams({ framework });
      if (entityId) p.set("entity_id", entityId);
      const r = await apiFetch(`${BASE}/history?${p.toString()}`);
      setHistory(r?.versions || []);
    } catch (e) {
      setError(e.message || "Failed to load history");
    } finally {
      setHistLoading(false);
    }
  }, [framework, entityId]);

  const toggleHistory = () => {
    const next = !histOpen;
    setHistOpen(next);
    if (next) loadHistory();
  };

  const submit = async (e) => {
    e.preventDefault();
    const imp = Number(form.impact_threshold);
    const fin = Number(form.financial_threshold);
    if (!form.framework.trim()) return setError("Framework is required");
    if (!form.effective_date) return setError("Effective date is required");
    for (const [name, v] of [["Impact", imp], ["Financial", fin]]) {
      if (!Number.isInteger(v) || v < SCALE_MIN || v > SCALE_MAX)
        return setError(`${name} threshold must be an integer within ${SCALE_MIN}–${SCALE_MAX}`);
    }
    setBusy(true);
    setError("");
    try {
      await apiFetch(BASE, {
        method: "POST",
        body: {
          framework: form.framework.trim(),
          entity_id: form.entity_id || null,
          impact_threshold: imp,
          financial_threshold: fin,
          effective_date: form.effective_date,
        },
      });
      await load();
      if (histOpen) await loadHistory();
    } catch (e2) {
      setError(e2.message || "Failed to set threshold");
    } finally {
      setBusy(false);
    }
  };

  const supersede = async (row) => {
    const scope = row.entity_id ? `${row.framework} · ${row.entity_id}` : `${row.framework} (framework default)`;
    if (!window.confirm(`Supersede the active threshold for ${scope}? Resolution will fall back down the chain.`))
      return;
    setBusy(true);
    setError("");
    try {
      await apiFetch(`${BASE}/${row.config_id}`, { method: "DELETE" });
      await load();
      if (histOpen) await loadHistory();
    } catch (e) {
      setError(e.message || "Failed to supersede");
    } finally {
      setBusy(false);
    }
  };

  const seedDefault = async () => {
    setBusy(true);
    setError("");
    try {
      await apiFetch(`${BASE}/seed-default`, { method: "POST" });
      await load();
    } catch (e) {
      setError(e.message || "Failed to seed global default");
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = (row) =>
    row.superseded
      ? <Badge c={C.neutral} label="Superseded" />
      : <Badge c={C.primary} label="Active" />;

  const configColumns = [
    { key: "framework", label: "Framework" },
    { key: "entity_id", label: "Entity", render: (r) => entityLabel(r.entity_id) },
    { key: "impact_threshold", label: "Impact", align: "right",
      render: (r) => <Data color={C.ink}>{r.impact_threshold}/25</Data> },
    { key: "financial_threshold", label: "Financial", align: "right",
      render: (r) => <Data color={C.ink}>{r.financial_threshold}/25</Data> },
    { key: "effective_date", label: "Effective" },
    { key: "superseded", label: "Status", render: statusBadge },
    { key: "_action", label: "", align: "right",
      render: (r) => r.superseded ? null : (
        <Btn variant="destructive" onClick={() => supersede(r)} disabled={busy}>Supersede</Btn>
      ) },
  ];

  const historyColumns = [
    { key: "impact_threshold", label: "Impact", align: "right",
      render: (r) => <Data color={C.ink}>{r.impact_threshold}/25</Data> },
    { key: "financial_threshold", label: "Financial", align: "right",
      render: (r) => <Data color={C.ink}>{r.financial_threshold}/25</Data> },
    { key: "effective_date", label: "Effective" },
    { key: "changed_by", label: "Changed by", render: (r) => <Data color={C.body}>{r.changed_by || "—"}</Data> },
    { key: "created_at", label: "Recorded",
      render: (r) => <Data color={C.muted}>{r.created_at || r.effective_date || "—"}</Data> },
    { key: "superseded", label: "Status", render: statusBadge },
  ];

  // Entity filter as an explicit action select so "framework-level" is a real option.
  const entitySelect = (
    <select
      value={entityId || ""}
      onChange={(e) => setEntityId(e.target.value)}
      aria-label="Entity scope"
      style={{ ...field, width: "auto", minWidth: 200 }}
    >
      <option value="">Framework-level (no entity)</option>
      {entities.map((en) => (
        <option key={en.entity_id} value={en.entity_id}>{en.name || en.entity_id}</option>
      ))}
    </select>
  );

  const frameworkInput = (
    <input
      value={frameworkDraft}
      onChange={(e) => setFrameworkDraft(e.target.value)}
      onBlur={() => setFramework(frameworkDraft.trim() || "esrs")}
      onKeyDown={(e) => { if (e.key === "Enter") setFramework(frameworkDraft.trim() || "esrs"); }}
      aria-label="Framework"
      placeholder="framework"
      style={{ ...field, width: 120 }}
    />
  );

  const cardGap = { display: "grid", gap: 16 };

  return (
    <Page>
      <PageHeader
        overline="Materiality Intelligence · Thresholds"
        OverlineIcon={SlidersHorizontal}
        title="Materiality Thresholds"
        subtitle="Impact and financial materiality cut-offs on the 1–25 IRO scoring scale, resolved entity → framework → global default."
        onRefresh={load}
        loading={loading}
        actions={<>{frameworkInput}{entitySelect}</>}
      />

      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {loading ? (
        <div style={cardGap}>
          <Skeleton h={120} />
          <Skeleton h={200} />
          <Skeleton h={180} />
        </div>
      ) : (
        <div style={cardGap}>
          {/* ── Active resolution ─────────────────────────────────────── */}
          <Card accent={C.primary}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <Overline Icon={SlidersHorizontal}>Active resolution</Overline>
              {resolved && <SourceBadge source={resolved.source} />}
            </div>
            <div style={{ display: "flex", gap: 48, flexWrap: "wrap", marginTop: 4 }}>
              <div>
                <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>Impact threshold</div>
                <Data size={28} color={C.ink}>{resolved?.impact_threshold ?? "—"}</Data>
                <span style={{ fontFamily: MONO, fontSize: 13, color: C.muted }}> /25</span>
              </div>
              <div>
                <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>Financial threshold</div>
                <Data size={28} color={C.ink}>{resolved?.financial_threshold ?? "—"}</Data>
                <span style={{ fontFamily: MONO, fontSize: 13, color: C.muted }}> /25</span>
              </div>
            </div>
            <p style={{ fontFamily: SANS, fontSize: 12, color: C.body, margin: "14px 0 0", lineHeight: 1.5 }}>
              Effective thresholds for framework <Data color={C.body}>{framework || "—"}</Data>
              {entityId ? <> · entity <Data color={C.body}>{entityId}</Data></> : " · framework level"}.
              Scores at or above the threshold are material.
            </p>
          </Card>

          {/* ── Configurations ────────────────────────────────────────── */}
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <Overline Icon={Layers}>Configurations</Overline>
              <Btn variant="secondary" Icon={Sprout} onClick={seedDefault} disabled={busy}>
                Seed global default (9/9)
              </Btn>
            </div>
            <DataTable
              columns={configColumns}
              rows={configs}
              keyField="config_id"
              emptyLabel="No active configurations for this filter"
            />
          </Card>

          {/* ── Set threshold ─────────────────────────────────────────── */}
          <Card>
            <Overline Icon={Plus}>Set threshold</Overline>
            <form onSubmit={submit}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                <div>
                  <Label>Framework *</Label>
                  <input
                    value={form.framework}
                    onChange={(e) => setF("framework", e.target.value)}
                    required
                    style={field}
                  />
                </div>
                <div>
                  <Label>Entity</Label>
                  <select
                    value={form.entity_id}
                    onChange={(e) => setF("entity_id", e.target.value)}
                    style={field}
                  >
                    <option value="">Framework-level (no entity)</option>
                    {entities.map((en) => (
                      <option key={en.entity_id} value={en.entity_id}>{en.name || en.entity_id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Impact threshold *</Label>
                  <input
                    type="number" min={SCALE_MIN} max={SCALE_MAX} step={1}
                    value={form.impact_threshold}
                    onChange={(e) => setF("impact_threshold", e.target.value)}
                    required
                    style={field}
                  />
                </div>
                <div>
                  <Label>Financial threshold *</Label>
                  <input
                    type="number" min={SCALE_MIN} max={SCALE_MAX} step={1}
                    value={form.financial_threshold}
                    onChange={(e) => setF("financial_threshold", e.target.value)}
                    required
                    style={field}
                  />
                </div>
                <div>
                  <Label>Effective date *</Label>
                  <input
                    type="date"
                    value={form.effective_date}
                    onChange={(e) => setF("effective_date", e.target.value)}
                    required
                    style={field}
                  />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginTop: 14 }}>
                <span style={{ fontFamily: SANS, fontSize: 12, color: C.body, lineHeight: 1.5, maxWidth: "60ch" }}>
                  1–25 IRO scoring scale; scores at or above the threshold are material.
                </span>
                <Btn type="submit" variant="primary" Icon={Plus} disabled={busy}>
                  {busy ? "Saving…" : "Set threshold"}
                </Btn>
              </div>
            </form>
          </Card>

          {/* ── History (collapsible) ─────────────────────────────────── */}
          <Card>
            <button
              type="button"
              onClick={toggleHistory}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left",
              }}
            >
              {histOpen ? <ChevronDown size={16} strokeWidth={1.5} color={C.body} /> : <ChevronRight size={16} strokeWidth={1.5} color={C.body} />}
              <Overline Icon={HistoryIcon} style={{ marginBottom: 0 }}>
                History{entityId ? " · entity" : " · framework level"}
              </Overline>
            </button>
            {histOpen && (
              <div style={{ marginTop: 14 }}>
                {histLoading ? (
                  <Skeleton h={120} />
                ) : (
                  <DataTable
                    columns={historyColumns}
                    rows={history}
                    keyField="config_id"
                    emptyLabel="No version history for this scope"
                  />
                )}
              </div>
            )}
          </Card>
        </div>
      )}
    </Page>
  );
}
